import { useEffect, useState } from 'react';

/**
 * Local expiry reminders — the only kind this client can honestly offer.
 *
 * There is no web push pipeline: the backend's push-devices endpoint accepts
 * Expo tokens (ios/android) only, so nothing a browser subscribes to would
 * ever be delivered. Until that exists server-side, a reminder here is a
 * Notification fired by this page's own timer, which means it can only fire
 * while the app is open. Every piece of UI copy must respect that limit.
 */

/** Mirrors the backend's EXPIRY_REMINDER_LEAD_MINUTES for the mobile push. */
export const REMINDER_LEAD_MINUTES = 15;

export type ReminderPermission = 'unsupported' | 'default' | 'granted' | 'denied';

export function getReminderPermission(): ReminderPermission {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

/**
 * Permission is the on/off state — there is no subscription behind it. The
 * driver can flip it in site settings without touching the page, so it is
 * re-read whenever the tab regains visibility.
 */
export function useReminderPermission(): {
  permission: ReminderPermission;
  request: () => Promise<ReminderPermission>;
} {
  const [permission, setPermission] = useState<ReminderPermission>(getReminderPermission);

  useEffect(() => {
    const onVisible = () => setPermission(getReminderPermission());
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const request = async (): Promise<ReminderPermission> => {
    if (typeof Notification === 'undefined') return 'unsupported';
    // Must run inside a user gesture or browsers auto-deny.
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  };

  return { permission, request };
}

// Keyed by session id so an extension (new expiresAt) replaces the pending
// timer instead of adding a second notification for the same stay.
const timers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Arms the T-minus-15-minutes notification for one session. No-op unless
 * permission is granted and the lead point is still in the future. The timer
 * dies with the page — that constraint is stated in the UI, never papered over.
 */
export function scheduleLocalReminder(
  sessionId: string,
  expiresAt: string,
  title: string,
  body: string,
): void {
  if (getReminderPermission() !== 'granted') return;
  const existing = timers.get(sessionId);
  if (existing !== undefined) clearTimeout(existing);
  const delay = new Date(expiresAt).getTime() - REMINDER_LEAD_MINUTES * 60_000 - Date.now();
  if (delay <= 0) {
    timers.delete(sessionId);
    return;
  }
  timers.set(
    sessionId,
    // Hidden-tab throttling can delay this by up to ~a minute; acceptable for
    // a 15-minute lead and covered by the "while this app is open" copy.
    setTimeout(() => {
      timers.delete(sessionId);
      void showNotification(title, body, sessionId);
    }, delay),
  );
}

async function showNotification(title: string, body: string, tag: string): Promise<void> {
  const options: NotificationOptions = { body, tag, icon: '/icon-192.png' };
  try {
    // Android Chrome forbids the page-context Notification constructor; the
    // registered PWA service worker is the path that works everywhere.
    const registration = await navigator.serviceWorker?.getRegistration();
    if (registration) {
      await registration.showNotification(title, options);
      return;
    }
  } catch {
    // fall through to the page-context constructor
  }
  try {
    new Notification(title, options);
  } catch {
    // Nothing left to try; the in-app countdown remains the fallback.
  }
}
